import { SIDE_NAMES } from '@halvard/board3d';
import { ice } from '@halvard/rules';
import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useStore } from 'zustand';
import { IceLocalGame } from '../game/ice/IceLocalGame';
import { attackText, outcomeLine } from '../game/ice/matchText';
import { createOnlineMatch, type CreateRequest, type OnlineMatch } from '../game/ice/online';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { iceServerUrl } from '../net/iceSocket';
import './PlayBot.css';

const TOKEN_KEY = 'halvard.iceGuest';

/** The guest token, kept between visits when the browser allows it. */
const tokens = {
  get: () => {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  },
  set: (token: string) => {
    try {
      localStorage.setItem(TOKEN_KEY, token);
    } catch {
      // Private windows: the guest just won't be remembered.
    }
  },
};

const serverUrl = () =>
  iceServerUrl(window.location, import.meta.env.VITE_ICE_SERVER as string | undefined);

export function inviteLink(origin: string, matchId: string, joinToken: string): string {
  return `${origin}/play/${matchId}?invite=${encodeURIComponent(joinToken)}`;
}

/** `/play/online`: set up a match against a friend, then play it (the URL becomes `/play/:id`). */
export function PlayOnlineSetup() {
  useDocumentTitle('Play a friend');
  const [request, setRequest] = useState<CreateRequest | null>(null);
  if (request) return <OnlineMatchView create={request} />;
  return <SetupForm onStart={setRequest} />;
}

/** `/play/:matchId`, with `?invite=…` the first time a friend opens the link. */
export function PlayOnlineMatch() {
  const { matchId } = useParams();
  const [params] = useSearchParams();
  useDocumentTitle('Online match');
  if (!matchId) return null;
  const invite = params.get('invite');
  return (
    <OnlineMatchView key={matchId} matchId={matchId} {...(invite ? { joinToken: invite } : {})} />
  );
}

function SetupForm({ onStart }: { onStart: (request: CreateRequest) => void }) {
  const [mode, setMode] = useState<CreateRequest['mode']>('match');
  const [side, setSide] = useState<CreateRequest['side']>('random');
  return (
    <article className="page-page bot-setup">
      <h1>Play a friend</h1>
      <p>
        Create a game, then send the link to your friend. Whoever opens it takes the other side.
      </p>
      <fieldset className="bot-picker">
        <legend>Format</legend>
        <div className="bot-strategies">
          <Choice
            pressed={mode === 'match'}
            onClick={() => setMode('match')}
            title="Match (recommended)"
            detail="Two games on the same lake: each of you attacks once."
          />
          <Choice
            pressed={mode === 'single'}
            onClick={() => setMode('single')}
            title="Single game"
            detail="One game: one attacks, one defends."
          />
        </div>
      </fieldset>
      <fieldset className="bot-picker">
        <legend>{mode === 'match' ? 'You attack first?' : 'Your side'}</legend>
        <div className="bot-strategies">
          <Choice
            pressed={side === 'random'}
            onClick={() => setSide('random')}
            title="Random"
            detail="Let the ice decide."
          />
          <Choice
            pressed={side === 'brand'}
            onClick={() => setSide('brand')}
            title="Brand"
            detail="Attack: take the runestones."
          />
          <Choice
            pressed={side === 'rime'}
            onClick={() => setSide('rime')}
            title="Rime"
            detail="Defend until the thaw."
          />
        </div>
      </fieldset>
      <p>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => onStart({ mode, side, lake: { kind: 'random' } })}
        >
          Create and get a link
        </button>
      </p>
    </article>
  );
}

function Choice(props: { pressed: boolean; onClick: () => void; title: string; detail: string }) {
  return (
    <button
      type="button"
      className="bot-strategy"
      aria-pressed={props.pressed}
      onClick={props.onClick}
    >
      <strong>{props.title}</strong>
      <span>{props.detail}</span>
    </button>
  );
}

function OnlineMatchView(props: { matchId?: string; joinToken?: string; create?: CreateRequest }) {
  const [match, setMatch] = useState<OnlineMatch | null>(null);
  const { matchId, joinToken, create } = props;

  useEffect(() => {
    const created = createOnlineMatch({
      url: serverUrl(),
      tokens,
      ...(matchId ? { matchId } : {}),
      ...(joinToken ? { joinToken } : {}),
      ...(create ? { create } : {}),
      // The page becomes the match's own address, without reloading it.
      onCreated: (id) => window.history.replaceState(null, '', `/play/${id}`),
    });
    setMatch(created);
    return () => created.close();
  }, [matchId, joinToken, create]);

  if (!match) return null;
  return <MatchScreen match={match} />;
}

function MatchScreen({ match }: { match: OnlineMatch }) {
  const m = useStore(match.meta);
  const g = useStore(match.game);
  const [copied, setCopied] = useState(false);
  const [confirmResign, setConfirmResign] = useState(false);
  const seat = m.role === 'host' || m.role === 'guest' ? m.role : null;

  // The invite token is only needed once: drop it from the address bar after joining.
  useEffect(() => {
    if (m.role === 'guest' && m.matchId && window.location.search.includes('invite=')) {
      window.history.replaceState(null, '', `/play/${m.matchId}`);
    }
  }, [m.role, m.matchId]);

  if (m.status === 'outdated') {
    return (
      <article className="page-page">
        <h1>This page is out of date</h1>
        <p>The game server has been updated. Reload the page to keep playing.</p>
        <button type="button" className="btn btn-primary" onClick={() => window.location.reload()}>
          Reload
        </button>
      </article>
    );
  }
  if (!m.role) {
    return (
      <article className="page-page">
        <h1>{m.status === 'offline' ? 'Can’t reach the game server' : 'Connecting…'}</h1>
        {m.error && <p>{m.error}</p>}
        {m.status === 'offline' && <p>Trying again…</p>}
      </article>
    );
  }

  const link =
    m.matchId && m.joinToken ? inviteLink(window.location.origin, m.matchId, m.joinToken) : null;
  const waiting = m.role === 'host' && !m.players.guest;
  const games = m.finished.map((f) => f.result);
  const mySide = seat ? ice.sideOf(m.firstBrand, m.index, seat) : null;
  const opponentSeat = seat ? ice.otherSeat(seat) : null;

  const copy = () => {
    if (!link) return;
    void navigator.clipboard?.writeText(link).then(() => setCopied(true));
  };

  const resultActions = m.upcoming ? (
    <>
      <p className="game-bannerDetail">
        {games[0] ? `${attackText(games[0], m.players, seat)}.` : ''} Now the sides swap
        {seat ? `: you ${ice.sideOf(m.firstBrand, 1, seat) === 'brand' ? 'attack' : 'defend'}` : ''}
        .
      </p>
      <button type="button" className="btn btn-primary" onClick={match.continueToNext}>
        Start game 2
      </button>
    </>
  ) : m.outcome ? (
    <>
      <p className="game-bannerDetail">
        <strong>{outcomeLine(m.outcome, games, m.players, seat).title}.</strong>{' '}
        {outcomeLine(m.outcome, games, m.players, seat).detail}
      </p>
      <Link to="/play/online" className="btn btn-primary">
        New match
      </Link>
    </>
  ) : null;

  const overlay = waiting ? (
    <div className="game-banner" role="status">
      <p className="game-bannerTitle">Waiting for your friend</p>
      <p className="game-bannerDetail">Send them this link. The game starts when they open it.</p>
      {link && (
        <div className="game-row">
          <input
            className="online-link"
            readOnly
            value={link}
            aria-label="Invite link"
            onFocus={(e) => e.currentTarget.select()}
          />
          <button type="button" className="btn btn-primary" onClick={copy}>
            {copied ? 'Copied' : 'Copy link'}
          </button>
        </div>
      )}
    </div>
  ) : null;

  const panelExtra = (
    <div className="game-section">
      <h2 className="label">
        {m.mode === 'match' ? `Match · game ${m.index + 1} of 2` : 'Single game'}
      </h2>
      <ul className="ice-stones" aria-label="Players">
        {(['host', 'guest'] as const).map((s) => (
          <li key={s}>
            <span
              className={`ice-dot ${m.connected[s] ? '' : 'ice-dotEmpty'}`}
              aria-hidden="true"
              style={m.connected[s] ? { background: '#3fbf7f' } : {}}
            />
            <span>
              {m.players[s]?.name ?? 'Waiting…'}
              {s === seat ? ' (you)' : ''}
            </span>
            <span className="game-hint">
              {m.players[s]
                ? m.connected[s]
                  ? SIDE_NAMES[ice.sideOf(m.firstBrand, m.index, s)]
                  : 'away'
                : ''}
            </span>
          </li>
        ))}
      </ul>
      {mySide && !m.outcome && (
        <p className="game-hint">
          You {mySide === 'brand' ? 'attack' : 'defend'} this game ({SIDE_NAMES[mySide]}).
          {opponentSeat && m.players[opponentSeat] && !m.connected[opponentSeat]
            ? ' Your opponent is away.'
            : ''}
        </p>
      )}
      {m.role === 'spectator' && <p className="game-hint">You are watching.</p>}
      {games.map((game, i) => (
        <p key={i} className="game-hint">
          Game {i + 1}: {attackText(game, m.players, seat)}.
        </p>
      ))}
      {m.status !== 'online' && <p className="ice-warn">Reconnecting…</p>}
      {m.error && <p className="game-hint">{m.error}</p>}
      {seat && !m.outcome && (
        <div className="game-row">
          {confirmResign ? (
            <button
              type="button"
              className="btn btn-small"
              onClick={() => {
                match.resign();
                setConfirmResign(false);
              }}
              onBlur={() => setConfirmResign(false)}
            >
              Confirm: resign{m.mode === 'match' ? ' the match' : ''}
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-small"
              onClick={() => setConfirmResign(true)}
              disabled={g.game.phase === 'over'}
            >
              Resign
            </button>
          )}
        </div>
      )}
    </div>
  );

  return (
    <IceLocalGame
      store={match.game}
      panelExtra={panelExtra}
      resultActions={resultActions}
      overlay={overlay}
      {...(waiting ? { status: 'Waiting for your friend' } : {})}
    />
  );
}
